import { useEffect, useState } from 'react';
import { Building2, ChevronDown, Plus } from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { useTenant, useIsSuperAdmin } from '@paulownia/shared/lib/tenant-context';
import { getTenantById, type Tenant } from '@paulownia/shared/services/tenant';
import { initFirebase } from '@paulownia/shared/lib/firebase';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@paulownia/shared/components/ui/dropdown-menu';
import { Button } from '@paulownia/shared/components/ui/button';

interface TenantSelectorProps {
  onCreateTenant?: () => void;
}

export function TenantSelector({ onCreateTenant }: TenantSelectorProps) {
  const { claims, currentTenantId, setCurrentTenantId, loading } = useTenant();
  const isSuperAdmin = useIsSuperAdmin();
  const [tenants, setTenants] = useState<Record<string, Partial<Tenant> & { id: string; name: string; slug: string }>>({});
  const [loadingTenants, setLoadingTenants] = useState(false);

  // Load tenant details
  useEffect(() => {
    const loadTenants = async () => {
      setLoadingTenants(true);

      try {
        // Superadmins load ALL tenants
        if (isSuperAdmin) {
          const { functions } = initFirebase();
          const listAllTenants = httpsCallable(functions, 'listAllTenants');
          const result = await listAllTenants();
          const data = result.data as { success: boolean; tenants: Array<{ id: string; name: string; slug: string }> };

          if (data.success) {
            const tenantsMap: Record<string, { id: string; name: string; slug: string }> = {};
            data.tenants.forEach((tenant) => {
              tenantsMap[tenant.id] = {
                id: tenant.id,
                name: tenant.name,
                slug: tenant.slug,
              };
            });
            setTenants(tenantsMap);
          }
        } else {
          // Regular users load only their tenants
          if (!claims?.tenantIds || claims.tenantIds.length === 0) {
            setLoadingTenants(false);
            return;
          }

          const results = await Promise.all(
            claims.tenantIds.map(async (tenantId) => {
              try {
                const tenant = await getTenantById(tenantId);
                return { tenantId, tenant };
              } catch (error) {
                console.error(`Failed to load tenant ${tenantId}:`, error);
                return { tenantId, tenant: null };
              }
            })
          );

          const tenantsMap: Record<string, Partial<Tenant> & { id: string; name: string; slug: string }> = {};
          results.forEach(({ tenantId, tenant }) => {
            if (tenant) {
              tenantsMap[tenantId] = tenant;
            }
          });
          setTenants(tenantsMap);
        }
      } catch (error) {
        console.error('Error loading tenants:', error);
      } finally {
        setLoadingTenants(false);
      }
    };

    if (claims) {
      loadTenants();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claims?.tenantIds, isSuperAdmin]);

  if (loading || !claims) {
    return null;
  }

  const tenantIds = isSuperAdmin ? Object.keys(tenants) : (claims.tenantIds || []);

  // No tenants - show create tenant button
  if (tenantIds.length === 0) {
    return (
      <Button
        variant="outline"
        size="sm"
        onClick={onCreateTenant}
        className="gap-2"
      >
        <Plus className="h-4 w-4" />
        Create Tenant
      </Button>
    );
  }

  // Single tenant - show tenant name only (but not for superadmins, they should see dropdown)
  if (tenantIds.length === 1 && !isSuperAdmin) {
    const tenantId = tenantIds[0];
    const tenant = tenants[tenantId];
    const role = claims.roles[tenantId];

    return (
      <div className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1.5 text-sm text-muted-foreground min-w-0">
        <Building2 className="h-4 w-4 flex-shrink-0" />
        <span className="font-medium truncate max-w-[80px] sm:max-w-[150px]">
          {loadingTenants ? '...' : tenant?.name || tenantId}
        </span>
        {role && (
          <span className="text-xs opacity-60 flex-shrink-0">
            ({role})
          </span>
        )}
      </div>
    );
  }

  // Multiple tenants or superadmin - show dropdown selector
  const currentTenant = currentTenantId ? tenants[currentTenantId] : null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <Building2 className="h-4 w-4" />
          <span className="max-w-[150px] truncate">
            {loadingTenants
              ? 'Loading...'
              : currentTenant?.name || 'Select Tenant'}
          </span>
          {isSuperAdmin && (
            <span className="text-xs text-purple-600 font-medium">★</span>
          )}
          <ChevronDown className="h-4 w-4 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[250px] max-h-[400px] overflow-y-auto bg-white">
        <DropdownMenuLabel>
          {isSuperAdmin ? 'Tous les tenants (Superadmin)' : 'Switch Tenant'}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {tenantIds
          .sort((a, b) => {
            const nameA = tenants[a]?.name || a;
            const nameB = tenants[b]?.name || b;
            return nameA.localeCompare(nameB);
          })
          .map((tenantId) => {
            const tenant = tenants[tenantId];
            const role = claims.roles?.[tenantId];
            const isActive = tenantId === currentTenantId;

            return (
              <DropdownMenuItem
                key={tenantId}
                onClick={() => setCurrentTenantId(tenantId)}
                className={isActive ? 'bg-accent' : ''}
              >
                <div className="flex flex-col gap-0.5 flex-1">
                  <span className="font-medium">
                    {tenant?.name || tenantId}
                  </span>
                  <div className="flex items-center gap-2">
                    {tenant?.slug && (
                      <span className="text-xs text-muted-foreground">
                        {tenant.slug}
                      </span>
                    )}
                    {role && (
                      <span className="text-xs text-muted-foreground">
                        • {role}
                      </span>
                    )}
                    {isSuperAdmin && !role && (
                      <span className="text-xs text-purple-600">
                        • superadmin
                      </span>
                    )}
                  </div>
                </div>
              </DropdownMenuItem>
            );
          })}
        {onCreateTenant && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onCreateTenant} className="gap-2">
              <Plus className="h-4 w-4" />
              Create New Tenant
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
